import { useState, useRef, useEffect } from "react";
import { MapPin, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Comprehensive list of 50+ UK + European cities for event search
const CITIES = [
  // UK & Ireland
  { city: "London", country: "UK", region: "England" },
  { city: "Manchester", country: "UK", region: "England" },
  { city: "Birmingham", country: "UK", region: "England" },
  { city: "Bristol", country: "UK", region: "England" },
  { city: "Leeds", country: "UK", region: "England" },
  { city: "Liverpool", country: "UK", region: "England" },
  { city: "Newcastle", country: "UK", region: "England" },
  { city: "Sheffield", country: "UK", region: "England" },
  { city: "Nottingham", country: "UK", region: "England" },
  { city: "Brighton", country: "UK", region: "England" },
  { city: "Glasgow", country: "UK", region: "Scotland" },
  { city: "Edinburgh", country: "UK", region: "Scotland" },
  { city: "Cardiff", country: "UK", region: "Wales" },
  { city: "Belfast", country: "UK", region: "N. Ireland" },
  { city: "Dublin", country: "Ireland", region: "" },
  
  // Germany
  { city: "Berlin", country: "Germany", region: "" },
  { city: "Munich", country: "Germany", region: "Bavaria" },
  { city: "Frankfurt", country: "Germany", region: "" },
  { city: "Hamburg", country: "Germany", region: "" },
  { city: "Cologne", country: "Germany", region: "" },
  { city: "Dusseldorf", country: "Germany", region: "" },
  { city: "Leipzig", country: "Germany", region: "" },
  
  // Netherlands
  { city: "Amsterdam", country: "Netherlands", region: "" },
  { city: "Rotterdam", country: "Netherlands", region: "" },
  { city: "Utrecht", country: "Netherlands", region: "" },
  { city: "Eindhoven", country: "Netherlands", region: "" },
  
  // France
  { city: "Paris", country: "France", region: "" },
  { city: "Lyon", country: "France", region: "" },
  { city: "Marseille", country: "France", region: "" },
  { city: "Bordeaux", country: "France", region: "" },
  { city: "Toulouse", country: "France", region: "" },
  { city: "Nantes", country: "France", region: "" },
  
  // Spain
  { city: "Barcelona", country: "Spain", region: "Catalonia" },
  { city: "Madrid", country: "Spain", region: "" },
  { city: "Ibiza", country: "Spain", region: "Balearic Islands" },
  { city: "Valencia", country: "Spain", region: "" },
  { city: "Seville", country: "Spain", region: "" },
  
  // Italy
  { city: "Milan", country: "Italy", region: "" },
  { city: "Rome", country: "Italy", region: "" },
  { city: "Naples", country: "Italy", region: "" },
  { city: "Turin", country: "Italy", region: "" },
  { city: "Florence", country: "Italy", region: "" },
  
  // Belgium
  { city: "Brussels", country: "Belgium", region: "" },
  { city: "Antwerp", country: "Belgium", region: "" },
  { city: "Ghent", country: "Belgium", region: "" },
  
  // Portugal
  { city: "Lisbon", country: "Portugal", region: "" },
  { city: "Porto", country: "Portugal", region: "" },
  
  // Austria & Switzerland
  { city: "Vienna", country: "Austria", region: "" },
  { city: "Zurich", country: "Switzerland", region: "" },
  { city: "Geneva", country: "Switzerland", region: "" },
  
  // Scandinavia
  { city: "Copenhagen", country: "Denmark", region: "" },
  { city: "Stockholm", country: "Sweden", region: "" },
  { city: "Oslo", country: "Norway", region: "" },
  { city: "Helsinki", country: "Finland", region: "" },
  
  // Eastern Europe
  { city: "Prague", country: "Czech Republic", region: "" },
  { city: "Warsaw", country: "Poland", region: "" },
  { city: "Krakow", country: "Poland", region: "" },
  { city: "Budapest", country: "Hungary", region: "" },
  { city: "Bucharest", country: "Romania", region: "" },
  
  // Greece & Croatia
  { city: "Athens", country: "Greece", region: "" },
  { city: "Zagreb", country: "Croatia", region: "" },
  { city: "Split", country: "Croatia", region: "" },
];

const CitySearchAutocomplete = ({ 
  value = "", 
  onChange, 
  onSelect, 
  placeholder = "Search city...",
  navigateOnSelect = true 
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [inputValue, setInputValue] = useState(value);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.length > 0) {
      const filtered = CITIES.filter(
        (c) =>
          c.city.toLowerCase().includes(inputValue.toLowerCase()) ||
          c.country.toLowerCase().includes(inputValue.toLowerCase())
      ).slice(0, 10);
      setSuggestions(filtered);
      setIsOpen(filtered.length > 0);
    } else {
      // Show popular cities when empty
      setSuggestions(CITIES.slice(0, 8));
      setIsOpen(false);
    }
    setHighlightedIndex(0);
  }, [inputValue]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown") {
        setIsOpen(true);
        setSuggestions(CITIES.slice(0, 8));
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelect = (cityData) => {
    setInputValue(cityData.city);
    setIsOpen(false);
    
    if (onSelect) {
      onSelect(cityData);
    }
    
    if (navigateOnSelect) {
      navigate(`/events?city=${encodeURIComponent(cityData.city)}`);
    }
    
    if (onChange) {
      onChange(cityData.city);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  // Group cities by country
  const groupedByCountry = suggestions.reduce((acc, city) => {
    const key = city.country;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(city);
    return acc;
  }, {});

  return (
    <div ref={wrapperRef} className="relative" style={{ overflow: 'visible' }}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            setSuggestions(inputValue ? suggestions : CITIES.slice(0, 8));
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full h-12 pl-10 pr-4 glass-solid rounded-lg text-foreground placeholder:text-muted-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          data-testid="city-search-autocomplete-input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
      </div>

      {isOpen && suggestions.length > 0 && (
        <div 
          className="autocomplete-dropdown absolute w-full mt-2 glass-strong rounded-lg shadow-2xl border border-border/50"
          style={{ zIndex: 9999, position: 'absolute' }}
          data-dropdown="true"
          data-testid="city-search-dropdown"
        >
          <div className="max-h-80 overflow-y-auto overscroll-contain touch-pan-y">
            {Object.entries(groupedByCountry).map(([country, cities]) => (
              <div key={country}>
                <div className="px-4 py-2 text-xs text-muted-foreground uppercase tracking-wider bg-muted/30 sticky top-0 font-mono">
                  {country}
                </div>
                {cities.map((cityData) => {
                  const flatIndex = suggestions.findIndex((s) => s.city === cityData.city);
                  const isHighlighted = flatIndex === highlightedIndex;
                  return (
                    <button
                      key={`${cityData.city}-${cityData.country}`}
                      onClick={() => handleSelect(cityData)}
                      onTouchEnd={(e) => { e.preventDefault(); handleSelect(cityData); }}
                      className={`w-full px-4 py-3 flex items-center gap-3 transition-colors active:bg-primary/30 ${
                        isHighlighted ? "bg-primary/20" : "hover:bg-muted/50"
                      }`}
                      data-testid={`city-option-${cityData.city.toLowerCase()}`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="font-medium text-foreground">{cityData.city}</div>
                        {cityData.region && (
                          <div className="text-xs text-muted-foreground">{cityData.region}</div>
                        )}
                      </div>
                      <Search className="w-4 h-4 text-muted-foreground" />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t border-border/50 bg-muted/20">
            <p className="text-xs text-muted-foreground text-center font-mono">
              50+ cities across UK & Europe
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CitySearchAutocomplete;
